"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  Check,
  CheckCircle,
  Clock,
  Send,
  ShieldCheck,
} from "lucide-react";

/**
 * Projektanfrage in drei Schritten.
 *
 * Alle Eingaben leben im React-State – NICHT im DOM. Die frühere Version
 * hat die Radio-Buttons der Schritte 1 und 2 beim Wechsel zu Schritt 3
 * aus dem DOM entfernt und dann per `form.querySelector(...)` ausgelesen:
 * Dienstleistung, Budget, Zeitrahmen und Beschreibung kamen dadurch immer
 * leer beim Server an, und die API lehnte jede Anfrage mit „Bitte wähle
 * eine Dienstleistung aus" ab. Deshalb hier kontrollierte Felder plus
 * Validierung pro Schritt.
 */

type ServiceOption = { slug: string; name: string };

const EXTRA_SERVICES: ServiceOption[] = [
  { slug: "mehrere", name: "Mehrere Dienstleistungen" },
  { slug: "unsicher", name: "Bin mir noch unsicher" },
];

const BUDGETS = [
  "Unter 2.000 €",
  "2.000 – 5.000 €",
  "5.000 – 10.000 €",
  "10.000 – 25.000 €",
  "Über 25.000 €",
  "Noch nicht festgelegt",
];

const TIMELINES = [
  "So schnell wie möglich",
  "Innerhalb von 4 Wochen",
  "1 – 3 Monate",
  "3 – 6 Monate",
  "Kein fester Zeitrahmen",
];

const STEPS = [
  { id: 1, label: "Leistung" },
  { id: 2, label: "Projekt" },
  { id: 3, label: "Kontakt" },
] as const;

const MAX_DESCRIPTION = 4000;

const inputClass =
  "w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-colors duration-100 focus:border-brand-500";
const inputStyle = {
  backgroundColor: "var(--bg)",
  borderColor: "var(--border)",
  color: "var(--text)",
} as const;

function OptionCard({
  name,
  value,
  label,
  checked,
  onChange,
  compact = false,
}: {
  name: string;
  value: string;
  label: string;
  checked: boolean;
  onChange: (value: string) => void;
  compact?: boolean;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-lg border transition-all duration-150 hover:border-brand-500 ${
        compact ? "p-3" : "p-4"
      }`}
      style={{
        borderColor: checked ? "var(--color-brand-500)" : "var(--border)",
        backgroundColor: checked ? "var(--bg-brand-subtle)" : "transparent",
        boxShadow: checked ? "0 0 0 1px var(--color-brand-500)" : undefined,
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-100"
        style={{
          borderColor: checked ? "var(--color-brand-500)" : "var(--border-secondary)",
          backgroundColor: checked ? "var(--color-brand-500)" : "transparent",
        }}
      >
        {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </span>
      <span
        className={`${compact ? "text-sm" : "text-sm font-medium"}`}
        style={{ color: "var(--text)" }}
      >
        {label}
      </span>
    </label>
  );
}

export function AnfrageClient({ services }: { services: ServiceOption[] }) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [submitted, setSubmitted] = useState(false);
  const [mailSent, setMailSent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stepError, setStepError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const [service, setService] = useState("");
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // Honeypot

  const allServices = [...services, ...EXTRA_SERVICES];

  const scrollToForm = () => {
    const el = formRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top < 0 || top > 160) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goTo = (next: 1 | 2 | 3) => {
    setStepError("");
    setError("");
    setStep(next);
    scrollToForm();
  };

  const next = () => {
    if (step === 1) {
      if (!service) {
        setStepError("Bitte wählen Sie zuerst eine Dienstleistung aus.");
        return;
      }
      goTo(2);
    } else if (step === 2) {
      goTo(3);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!service) {
      goTo(1);
      setStepError("Bitte wählen Sie zuerst eine Dienstleistung aus.");
      return;
    }
    if (!consent) {
      setError("Bitte stimmen Sie der Verarbeitung Ihrer Daten zu.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/anfrage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service,
          budget,
          timeline,
          description,
          name,
          company,
          email,
          phone,
          website,
          consent,
          source: "Anfrage-Formular (/anfrage)",
        }),
      });

      const json = (await res.json().catch(() => ({}))) as {
        error?: string;
        mail_sent?: boolean;
      };
      if (!res.ok) {
        throw new Error(json.error || "Fehler beim Senden. Bitte versuchen Sie es erneut.");
      }
      setMailSent(json.mail_sent !== false);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-20">
        <div className="mx-auto max-w-lg px-4 text-center">
          <CheckCircle className="mx-auto h-20 w-20 text-success" aria-hidden="true" />
          <h1 className="mt-6 text-3xl font-bold" style={{ color: "var(--text)" }}>
            Anfrage erhalten!
          </h1>
          <p className="mt-4 text-lg" style={{ color: "var(--text-secondary)" }}>
            Vielen Dank, {name.trim().split(" ")[0]}. Wir sichten Ihre Angaben und melden uns
            innerhalb von 48 Stunden mit einer ersten Einschätzung.
          </p>
          <p className="mt-3 text-sm" style={{ color: "var(--text-tertiary)" }}>
            {mailSent
              ? `Eine Bestätigung ist unterwegs an ${email}.`
              : "Ihre Anfrage ist bei uns gespeichert. Die Bestätigungs-E-Mail konnte gerade nicht versendet werden – wir melden uns trotzdem."}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              href="/beratung"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-brand-600"
            >
              <CalendarCheck className="h-4 w-4" aria-hidden="true" />
              Direkt einen Gesprächstermin buchen
            </Link>
            <Link
              href="/"
              className="inline-flex items-center justify-center rounded-lg border px-5 py-2.5 text-sm font-medium transition-colors duration-150 hover:bg-gray-100"
              style={{ borderColor: "var(--border)", color: "var(--text)" }}
            >
              Zur Startseite
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const serviceLabel = allServices.find((s) => s.slug === service || s.name === service)?.name ?? service;

  return (
    <section className="py-16">
      <div className="mx-auto max-w-2xl px-4 lg:px-8">
        <div className="text-center">
          <p className="text-sm font-semibold text-brand-500">Projektanfrage</p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl" style={{ color: "var(--text)" }}>
            Projekt anfragen
          </h1>
          <p className="mt-4 text-base" style={{ color: "var(--text-secondary)" }}>
            Erzählen Sie uns von Ihrem Projekt. In drei kurzen Schritten haben wir alles, was wir für
            eine erste Einschätzung und ein individuelles Angebot brauchen.
          </p>
          <div
            className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs"
            style={{ color: "var(--text-tertiary)" }}
          >
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" /> ca. 2 Minuten
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Unverbindlich &amp; kostenlos
            </span>
          </div>
        </div>

        {/* Fortschritt */}
        <ol className="mt-10 flex items-center justify-center" aria-label="Fortschritt">
          {STEPS.map((s, i) => {
            const done = s.id < step;
            const active = s.id === step;
            return (
              <li key={s.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => done && goTo(s.id as 1 | 2 | 3)}
                  disabled={!done}
                  aria-current={active ? "step" : undefined}
                  className="flex items-center gap-2 disabled:cursor-default"
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-150 ${
                      done || active ? "bg-brand-500 text-white" : "border"
                    }`}
                    style={done || active ? undefined : { borderColor: "var(--border)", color: "var(--text-quaternary)" }}
                  >
                    {done ? <Check className="h-4 w-4" aria-hidden="true" /> : s.id}
                  </span>
                  <span
                    className="hidden text-sm font-medium sm:block"
                    style={{ color: active ? "var(--text)" : "var(--text-tertiary)" }}
                  >
                    {s.label}
                  </span>
                </button>
                {i < STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="mx-3 h-0.5 w-8 rounded-full transition-colors duration-300 sm:w-12"
                    style={{ backgroundColor: done ? "var(--color-brand-500)" : "var(--border)" }}
                  />
                )}
              </li>
            );
          })}
        </ol>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="mt-8 scroll-mt-24 rounded-2xl border p-6 sm:p-8"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--card-bg)",
            boxShadow: "var(--card-shadow-md)",
          }}
        >
          {/* Schritt 1: Dienstleistung */}
          {step === 1 && (
            <fieldset>
              <legend className="text-xl font-bold" style={{ color: "var(--text)" }}>
                Welche Dienstleistung interessiert Sie?
              </legend>
              <p className="mt-1 text-sm" style={{ color: "var(--text-tertiary)" }}>
                Wählen Sie den Bereich, der am besten passt.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {allServices.map((s) => (
                  <OptionCard
                    key={s.slug}
                    name="service"
                    value={s.name}
                    label={s.name}
                    checked={service === s.name}
                    onChange={(v) => {
                      setService(v);
                      setStepError("");
                    }}
                  />
                ))}
              </div>
              {stepError && (
                <p className="mt-3 text-sm font-medium" style={{ color: "var(--color-error)" }} role="alert">
                  {stepError}
                </p>
              )}
              <button
                type="button"
                onClick={next}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-500 px-6 py-3 text-base font-semibold text-white transition-colors duration-150 hover:bg-brand-600"
              >
                Weiter
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </fieldset>
          )}

          {/* Schritt 2: Budget, Zeitrahmen, Beschreibung */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold" style={{ color: "var(--text)" }}>
                Budget &amp; Zeitrahmen
              </h2>
              <p className="mt-1 text-sm" style={{ color: "var(--text-tertiary)" }}>
                Alle Angaben in diesem Schritt sind optional – sie helfen uns aber, ein passendes
                Angebot vorzubereiten.
              </p>
              <div className="mt-5 space-y-6">
                <fieldset>
                  <legend className="mb-2 block text-sm font-medium" style={{ color: "var(--text)" }}>
                    Budgetrahmen
                  </legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {BUDGETS.map((b) => (
                      <OptionCard
                        key={b}
                        compact
                        name="budget"
                        value={b}
                        label={b}
                        checked={budget === b}
                        onChange={setBudget}
                      />
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="mb-2 block text-sm font-medium" style={{ color: "var(--text)" }}>
                    Zeitrahmen
                  </legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {TIMELINES.map((t) => (
                      <OptionCard
                        key={t}
                        compact
                        name="timeline"
                        value={t}
                        label={t}
                        checked={timeline === t}
                        onChange={setTimeline}
                      />
                    ))}
                  </div>
                </fieldset>
                <div>
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <label htmlFor="details" className="block text-sm font-medium" style={{ color: "var(--text)" }}>
                      Projektbeschreibung
                    </label>
                    <span className="text-xs" style={{ color: "var(--text-quaternary)" }}>
                      {description.length}/{MAX_DESCRIPTION}
                    </span>
                  </div>
                  <textarea
                    id="details"
                    name="details"
                    rows={5}
                    maxLength={MAX_DESCRIPTION}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Worum geht es? Was soll am Ende stehen? Gibt es eine bestehende Website oder Vorgaben?"
                    className={`${inputClass} resize-y`}
                    style={inputStyle}
                  />
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => goTo(1)}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border px-6 py-3 text-base font-medium transition-colors duration-150 hover:bg-gray-100"
                  style={{ borderColor: "var(--border)", color: "var(--text)" }}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Zurück
                </button>
                <button
                  type="button"
                  onClick={next}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-500 px-6 py-3 text-base font-semibold text-white transition-colors duration-150 hover:bg-brand-600"
                >
                  Weiter
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* Schritt 3: Kontakt + Zusammenfassung */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold" style={{ color: "var(--text)" }}>
                Ihre Kontaktdaten
              </h2>

              <dl
                className="mt-4 grid gap-x-6 gap-y-2 rounded-lg border p-4 text-sm sm:grid-cols-3"
                style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
              >
                <div>
                  <dt style={{ color: "var(--text-quaternary)" }}>Dienstleistung</dt>
                  <dd className="font-medium" style={{ color: "var(--text)" }}>
                    {serviceLabel || "–"}
                  </dd>
                </div>
                <div>
                  <dt style={{ color: "var(--text-quaternary)" }}>Budget</dt>
                  <dd className="font-medium" style={{ color: "var(--text)" }}>
                    {budget || "Offen"}
                  </dd>
                </div>
                <div>
                  <dt style={{ color: "var(--text-quaternary)" }}>Zeitrahmen</dt>
                  <dd className="font-medium" style={{ color: "var(--text)" }}>
                    {timeline || "Offen"}
                  </dd>
                </div>
                <div className="sm:col-span-3">
                  <button
                    type="button"
                    onClick={() => goTo(1)}
                    className="text-xs font-medium text-brand-500 hover:underline"
                  >
                    Angaben ändern
                  </button>
                </div>
              </dl>

              <div className="mt-5 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="anfrage-name" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      id="anfrage-name"
                      name="name"
                      required
                      minLength={2}
                      maxLength={120}
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="anfrage-company" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                      Unternehmen
                    </label>
                    <input
                      type="text"
                      id="anfrage-company"
                      name="company"
                      maxLength={160}
                      autoComplete="organization"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="anfrage-email" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="anfrage-email"
                      name="email"
                      required
                      maxLength={200}
                      autoComplete="email"
                      inputMode="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label htmlFor="anfrage-phone" className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text)" }}>
                      Telefon (optional)
                    </label>
                    <input
                      type="tel"
                      id="anfrage-phone"
                      name="phone"
                      maxLength={50}
                      autoComplete="tel"
                      inputMode="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {/* Honeypot – für Menschen unsichtbar */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="anfrage-website">Website</label>
                  <input
                    type="text"
                    id="anfrage-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </div>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="consent"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded accent-brand-500"
                  />
                  <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    Ich habe die{" "}
                    <Link href="/datenschutz" className="underline hover:text-brand-500">
                      Datenschutzerklärung
                    </Link>{" "}
                    gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung dieser Anfrage zu. *
                  </span>
                </label>
              </div>

              {error && (
                <p className="mt-3 text-sm font-medium" style={{ color: "var(--color-error)" }} role="alert">
                  {error}
                </p>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => goTo(2)}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border px-6 py-3 text-base font-medium transition-colors duration-150 hover:bg-gray-100"
                  style={{ borderColor: "var(--border)", color: "var(--text)" }}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  Zurück
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-500 px-6 py-3 text-base font-semibold text-white transition-colors duration-150 hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-5 w-5" aria-hidden="true" />
                  {loading ? "Wird gesendet …" : "Anfrage senden"}
                </button>
              </div>
              <p className="mt-4 text-center text-xs" style={{ color: "var(--text-quaternary)" }}>
                Lieber direkt sprechen?{" "}
                <Link href="/beratung" className="font-medium text-brand-500 hover:underline">
                  Kostenloses Erstgespräch buchen
                </Link>
              </p>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
